import { Controller } from "@hotwired/stimulus";
import { browserAvailability } from "./demo_model_picker_controller";
import { prepareNano, releaseNano } from "../lib/demo_nano_session";

export default class extends Controller {
  static targets = ["config", "result", "status", "cancel", "timing"];

  async connect() {
    const availability = await browserAvailability();
    if (!this.element.isConnected) return;
    this.statusTarget.textContent =
      availability === "unavailable"
        ? "Gemini Nano isn't available here. Choose Backend from All experiments to continue."
        : availability === "available"
          ? "Gemini Nano · On-device analysis"
          : "Gemini Nano · Sending starts the first-time model download.";
  }

  progress(message) {
    this.statusTarget.textContent = message;
    this.dispatch("progress", { detail: { message } });
  }

  disconnect() {
    if (this.warm) this.warm.onProgress = null;
    this.abort?.abort();
    this.session?.destroy();
  }

  cancel() {
    this.abort?.abort();
    if (this.warm && !this.warm.prepared) releaseNano();
  }

  async abortable(promise) {
    const signal = this.abort.signal;
    let onAbort;
    try {
      return await Promise.race([
        promise,
        new Promise((_, reject) => {
          onAbort = () => reject(new DOMException("Stopped", "AbortError"));
          if (signal.aborted) onAbort();
          else signal.addEventListener("abort", onAbort, { once: true });
        }),
      ]);
    } finally {
      signal.removeEventListener("abort", onAbort);
    }
  }

  async submit(event) {
    const optional = this.element.dataset.optionalAnalysis === "true";
    const currentText = this.element.querySelector('[name="draft_check_in[message]"]').value;
    const completionPattern = this.element.dataset.needsCompletionPattern;
    if (completionPattern && new RegExp(completionPattern, "i").exec(currentText.trim())?.[0] === currentText.trim()) return;
    if (this.ready && this.analyzedText === currentText) return;
    this.ready = false;
    this.resultTarget.value = "";
    this.timingTarget.value = "";
    event.preventDefault();
    if (this.busy) return;
    if (!globalThis.LanguageModel && !optional) {
      this.statusTarget.textContent =
        "Gemini Nano isn't available here. Choose Backend from All experiments to continue.";
      this.dispatch("failure", {
        detail: { message: this.statusTarget.textContent },
      });
      return;
    }
    this.busy = true;
    this.dispatch("start");
    this.abort = new AbortController();
    const input = this.element.querySelector('[name="draft_check_in[message]"]');
    const button = this.element.querySelector('[type="submit"]');
    input.readOnly = true;
    button.disabled = true;
    this.cancelTarget.hidden = false;
    this.progress("Preparing Gemini Nano…");
    const timeout = setTimeout(() => this.cancel(), optional ? 20000 : 300000);
    const started = performance.now();

    try {
      const config = JSON.parse(this.configTarget.value);
      this.warm = prepareNano(config);
      this.warm.onProgress = (message) => this.progress(message);
      this.progress(this.warm.message);
      const base = await this.abortable(this.warm.promise);
      if (this.abort.signal.aborted)
        throw new DOMException("Stopped", "AbortError");
      const signal = this.abort.signal;
      this.session = await this.abortable(base.clone({ signal }).then((session) => {
        if (signal.aborted) {
          session.destroy();
          throw new DOMException("Stopped", "AbortError");
        }
        return session;
      }));
      const prepared = performance.now();
      this.progress(optional ? "Preparing the educator preview…" : "Reading your response on this device…");
      const raw = await this.abortable(this.session.prompt(
        config.prompt.replace(
          "__STUDENT_NOTE__",
          () =>
            (this.element.querySelector('[name="analysis_context"]')?.value ||
              "") + input.value.trim(),
        ),
        { responseConstraint: config.schema, signal: this.abort.signal },
      ));
      if (this.abort.signal.aborted || !this.element.isConnected)
        throw new DOMException("Stopped", "AbortError");
      const processed = performance.now();
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
        throw new Error("Invalid analysis");
      this.resultTarget.value = JSON.stringify(parsed);
      this.timingTarget.value = JSON.stringify({
        setup_ms: Math.round(this.warm.prepared - this.warm.started),
        setup_wait_ms: Math.round(prepared - started),
        setup_overlap_ms: Math.round(
          Math.max(
            0,
            Math.min(started, this.warm.prepared) - this.warm.started,
          ),
        ),
        inference_ms: Math.round(processed - prepared),
        total_ms: Math.round(processed - started),
        download_observed: this.warm.downloadObserved,
      });
      this.analyzedText = currentText;
      this.ready = true;
    } catch (error) {
      // Keep diagnostics free of student text and generated model output.
      console.warn("Gemini Nano analysis failed", JSON.stringify({
        name: error.name,
        aborted: this.abort.signal.aborted,
        elapsed_ms: Math.round(performance.now() - started),
      }));
      // A browser may evict an idle session. A retry must get a fresh baseline.
      releaseNano();
      if (optional) {
        // Grading may be unavailable, but confirmed answers still advance.
        this.resultTarget.value = JSON.stringify({ unavailable: true });
        this.analyzedText = currentText;
        this.ready = true;
      } else {
      this.statusTarget.textContent =
        error.name === "AbortError"
          ? "Stopped. Your text is still here. You can retry."
          : "Gemini Nano couldn't analyze this response. Your text is still here. Retry or choose Backend from All experiments.";
      this.dispatch("failure", {
        detail: { message: this.statusTarget.textContent },
      });
      }
    } finally {
      clearTimeout(timeout);
      if (this.warm) this.warm.onProgress = null;
      this.session?.destroy();
      this.session = null;
      input.readOnly = false;
      button.disabled = false;
      this.cancelTarget.hidden = true;
      this.busy = false;
    }
    if (this.ready && this.element.isConnected) this.element.requestSubmit();
  }
}
